import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV_LINKS, SITE } from "@/config/site";
import { cn } from "@/utils/format";

export default function SiteNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-300",
        scrolled || menuOpen
          ? "border-b border-background-200/70 bg-background-50/95 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-background-950/80 to-transparent"
      )}
    >
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link
          to="/"
          className="flex shrink-0 cursor-pointer items-center gap-2.5"
          aria-label={`${SITE.name} 首页`}
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-radar-line text-lg" aria-hidden="true" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse-ring rounded-full bg-accent-500" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold tracking-wide text-foreground-950 md:text-lg">
              爆料
              <span className="text-primary-500">直播</span>
            </span>
            <span className="mt-0.5 hidden font-label text-[10px] uppercase tracking-[0.3em] text-foreground-500 sm:block">
              {SITE.englishName}
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.to === "/"}
                  title={link.desc}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex cursor-pointer items-center whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-background-100 text-primary-500"
                        : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                    )
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-2 rounded-full border border-secondary-300/50 bg-secondary-100/40 px-3 py-1.5 font-label text-[11px] text-secondary-700 xl:inline-flex">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-secondary-500" aria-hidden="true" />
            {SITE.onlineSignals}
          </span>
          <Link
            to="/live"
            className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            <i className="ri-live-line" aria-hidden="true" />
            进入直播间
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "关闭导航菜单" : "打开导航菜单"}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/60 text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-500 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <ul className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "flex cursor-pointer flex-col rounded-md px-3 py-3 transition-colors",
                      isActive ? "bg-background-100" : "hover:bg-background-100"
                    )
                  }
                >
                  <span className="text-sm font-medium text-foreground-950">{link.label}</span>
                  <span className="mt-0.5 text-xs text-foreground-500">{link.desc}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          <Link
            to="/live"
            className="mt-4 flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-live-line" aria-hidden="true" />
            进入直播间
          </Link>
        </div>
      )}
    </header>
  );
}